import { useFormikContext } from 'formik'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { useNavigate } from '@lib/react-router-dom'

import { DepositFormWarningBox } from '@pages/Traders-Room/DepositFormWarningBox'
import { CurrencyAmountField } from '@pages/Traders-Room/Wallets/WalletDeposit/WalletDepositForm/CurrencyAmountField'
import { WalletFormFieldsFactory } from '@pages/Traders-Room/Wallets/WalletDeposit/WalletDepositForm/WalletFormFieldsFactory'
import { CardPaymentProviderProceedModal } from '@pages/Traders-Room/modals/CardPaymentProviderProceed.modal'
import { CryptoPaymentProviderProceedModal } from '@pages/Traders-Room/modals/CryptoPaymentProviderProceed.modal'
import { PaymentProviderProceedModal } from '@pages/Traders-Room/modals/PaymentProviderProceed.modal'

import { useModal } from '@context/ModalsContext'

import { Button } from '@ui/components/Button'
import { CaretIcon } from '@ui/icons/Caret.icon'

import {
  creditDebitCardPaymentProviderMethodId,
  isPaymentProviderBankType,
  isPaymentProviderCardProviderType,
  isPaymentProviderCryptoType,
  isPaymentProviderPSPType,
  isPaymentProviderPaymentAgentType,
  unionPayPaymentProviderMethodId,
} from '@model/PaymentProviderType'
import type { WalletDepositFormValues } from '@model/WalletDepositFormValues'

import { FieldGroup } from '@global/form/FieldGroup'
import { createFormField } from '@global/formField/FormField'
import { TransactionFormActionButtonTemplate } from '@global/templates/TransactionFormActionButtonTemplate'

import { CurrencyAmountChip } from '@shared/components/CurrencyAmountChip/CurrencyAmountChip'
import { FundingTermsConditions } from '@shared/components/Funding/FundingTermsConditions/FundingTermsConditions'
import { RadioModal } from '@shared/modals/Radio.modal'

import styles from './FirstTimeDepositForm.module.scss'

const FormField = createFormField<WalletDepositFormValues>()

interface FirstTimeDepositPSPFieldsProps {
  showAmountPresets?: boolean
  minAmount?: number
  skipButtonLink: string
}

export const FirstTimeDepositPSPFields: React.FC<FirstTimeDepositPSPFieldsProps> = (props) => {
  const { minAmount, showAmountPresets, skipButtonLink } = props
  const { t } = useTranslation()
  const navigate = useNavigate()

  const { values, setValues, isValid, handleSubmit } = useFormikContext<WalletDepositFormValues>()

  const cardPaymentProviderProceedModal = useModal(CardPaymentProviderProceedModal, {
    onProceed: handleSubmit,
  })

  const cryptoPaymentProviderProceedModal = useModal(CryptoPaymentProviderProceedModal, {
    onProceed: handleSubmit,
  })

  const paymentProviderProceedModal = useModal(PaymentProviderProceedModal, {
    onProceed: handleSubmit,
  })

  const currencyOptions = useMemo(
    () => values.paymentProvider?.parameters?.currencies?.map((c) => c.id) ?? [],
    [values.paymentProvider?.parameters?.currencies]
  )

  const currencyModal = useModal(RadioModal, {
    title: t('Wallet.Currency'),
    onSelect: (id: string) => {
      const selected = values.paymentProvider?.parameters?.currencies?.find((c) => c.id === id)
      if (!selected) return
      setValues({
        ...values,
        paymentProvider: {
          ...values.paymentProvider,
          currency: selected,
        },
      })
    },
    initialValue: values.paymentProvider?.currency?.id,
    simpleOptions: currencyOptions,
  })

  const handlePaymentProviderProceed = () => {
    if (isPaymentProviderCryptoType(values.paymentProvider?.providerCategory.id)) {
      cryptoPaymentProviderProceedModal.open({ values })
    } else if (isPaymentProviderPSPType(values.paymentProvider?.providerCategory.id)) {
      paymentProviderProceedModal.open({ values })
    }

    if (isPaymentProviderCardProviderType(values.paymentProvider.providerCategory.id)) {
      if (
        values.paymentProvider?.id === creditDebitCardPaymentProviderMethodId ||
        values.paymentProvider?.id === unionPayPaymentProviderMethodId
      ) {
        cardPaymentProviderProceedModal.open({ values })
      } else {
        paymentProviderProceedModal.open({ values })
      }
    }

    if (isPaymentProviderBankType(values.paymentProvider?.providerCategory.id)) {
      handleSubmit()
    }

    if (isPaymentProviderPaymentAgentType(values.paymentProvider?.providerCategory.id)) {
      handleSubmit()
    }
  }

  const disabled =
    values?.paymentProvider?.parameters &&
    !(values.paymentProvider.parameters.currencies.length > 1)

  return (
    <React.Fragment>
      <WalletFormFieldsFactory />
      <FieldGroup>
        <CurrencyAmountField />
        <div className={styles.currencyFieldWrapper}>
          <FormField
            name='paymentProvider.currency.id'
            label={t('Wallet.Currency')}
            disabled={disabled}
            readOnly
            rightIcon={!disabled ? CaretIcon.Down : undefined}
            onClick={() => {
              if (!disabled) currencyModal.open()
            }}
          />
        </div>
      </FieldGroup>
      {showAmountPresets && (
        <FieldGroup>
          <CurrencyAmountChip minValue={minAmount} />
        </FieldGroup>
      )}
      {values.paymentProvider.name === 'Crypto Currency' && (
        <DepositFormWarningBox className={styles.formItem}>
          {t('Crypto info')}
        </DepositFormWarningBox>
      )}
      <FormField name='comment' label={t('Wallet.Additional Comment')} />
      <DepositFormWarningBox className={styles.formItem}>
        <ul>
          <li>{t('Sign up.1. Start Now: Deposit now and start exploring your Client Area')}</li>
          <li>{t('Sign up.2. Note: Trade on your demo account until your account is verified')}</li>
          <li>
            {t(
              `Sign up.3. Full Access: Once your account is approved, you'll be able to trade with real funds and withdraw profits`
            )}
          </li>
        </ul>
      </DepositFormWarningBox>

      <FundingTermsConditions />

      <TransactionFormActionButtonTemplate>
        <Button.Secondary size='lg' fullWidth onClick={() => navigate(skipButtonLink)}>
          {t('Skip')}
        </Button.Secondary>
        <Button.Primary size='lg' disabled={!isValid} onClick={handlePaymentProviderProceed}>
          {t('Confirm')}
        </Button.Primary>
      </TransactionFormActionButtonTemplate>
    </React.Fragment>
  )
}
